import { styled } from "styled-components";

import { UISwitch } from "common/components";
import type { Setter } from "common/types/setter";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { InfoTooltip } from "domains/collection/components/edit/parts/Tooltip";
import type { Permissions } from "domains/collection/components/edit/types/permissions";
import { getCanFeatureCollections, getCanListCollections } from "entities/permissions/selectors";

const StyledRow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.textBright};
  display: flex;
  font-weight: bold;
  gap: 1rem;
  justify-content: space-between;
  width: 100%;
`;

const StyledLabel = styled.div`
  display: flex;
  gap: 1rem;
  position: relative;
`;

const StyledInfoTooltip = styled(InfoTooltip)`
  font-weight: normal;
  height: 18px;
`;

type CollectionUpdatePermissionsProps = { permissions: Permissions; setPermissions: Setter<Permissions> };

export const CollectionUpdatePermissions = ({ permissions, setPermissions }: CollectionUpdatePermissionsProps) => {
  const canFeature = useSelector(getCanFeatureCollections);
  const canList = useSelector(getCanListCollections);
  const translate = useTranslate();

  const { isFeatured, isListed, isPublic } = permissions;

  const toggleIsFeatured = () =>
    setPermissions({ isFeatured: !isFeatured, isListed, isPublic: !(isFeatured || isListed) || isPublic });

  const toggleIsListed = () =>
    setPermissions({ isFeatured, isListed: !isListed, isPublic: !(isFeatured || isListed) || isPublic });

  const toggleIsPublic = () =>
    setPermissions({ isFeatured: !isPublic && isFeatured, isListed: !isPublic && isListed, isPublic: !isPublic });

  return (
    <>
      {canFeature && (
        <StyledRow>
          <StyledLabel>
            <div>{translate("app.create.collection.featured")}</div>
            <StyledInfoTooltip maxTooltipWidth={240} tooltipId="featured-tooltip">
              {translate("app.create.collection.featured.tooltip")}
            </StyledInfoTooltip>
          </StyledLabel>
          <UISwitch aria-labelledby="featured-tooltip" isOn={isFeatured} onClick={toggleIsFeatured} />
        </StyledRow>
      )}
      {canList && (
        <StyledRow>
          <StyledLabel>
            <div>{translate("app.create.collection.listed")}</div>
            <StyledInfoTooltip maxTooltipWidth={240} tooltipId="listed-tooltip">
              {translate("app.create.collection.listed.tooltip")}
            </StyledInfoTooltip>
          </StyledLabel>
          <UISwitch aria-labelledby="listed-tooltip" isOn={isListed} onClick={toggleIsListed} />
        </StyledRow>
      )}
      <StyledRow>
        <StyledLabel>
          <div>{translate("app.create.collection.public")}</div>
          <StyledInfoTooltip maxTooltipWidth={272} tooltipId="public-tooltip">
            {translate("app.create.collection.public.tooltip")}
          </StyledInfoTooltip>
        </StyledLabel>
        <UISwitch aria-labelledby="public-tooltip" isOn={isPublic} onClick={toggleIsPublic}></UISwitch>
      </StyledRow>
    </>
  );
};
